import * as React from 'react';

import URI from 'jsuri';
import Q from 'q';

import API from 'js/lib/api';

import { useAllAdminsQuery, useTemplateIdsQuery } from 'bundles/enterprise-admin-management/hooks';
import { ThirdPartyOrganizationProviderBase } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';
import Button from 'bundles/internal-ui/components/buttons/Button';
import Card from 'bundles/internal-ui/components/cards/Card';
import TextBox from 'bundles/internal-ui/components/forms/TextBox';
import Table from 'bundles/internal-ui/components/table/Table';

import _t from 'i18n!nls/internal-enterprise';

import 'css!./__styles__/EnterpriseToolsHome';

const programsAPI = API('/api/enterprisePrograms.v1', { type: 'rest' });

type State = {
  errorMessage: string | null;
  slug: string;
  programId?: string;
  thirdPartyOrganizationId?: string;
};

class EnterpriseProgramLookup extends React.Component<{}, State> {
  state: State = {
    errorMessage: null,
    slug: '',
  };

  handleUpdateText = (event: React.ChangeEvent<any>) => {
    this.setState({ slug: event.target.value });
  };

  onInputKeyPress = (e: React.KeyboardEvent<any>) => {
    if (e.key === 'Enter') this.onViewProgramDetails();
  };

  onViewProgramDetails = () => {
    const { slug } = this.state;
    const uri = new URI().addQueryParam('q', 'slug').addQueryParam('slug', slug);
    Q(programsAPI.get(uri.toString()))
      .then((res) => {
        const program = res.elements[0];
        const programId = program.id;
        const thirdPartyOrganizationId = program.thirdPartyOrganizationId;
        this.setState({ programId, thirdPartyOrganizationId });
      })
      .fail((error) => {
        let errorMessage = '';
        if (error.status === 404) {
          errorMessage = _t('Program not found');
        } else {
          errorMessage = error.responseJson.msg;
        }
        this.setState({ slug: '', errorMessage });
      });
  };

  columns = [
    {
      key: 'id',
      name: _t('UID'),
    },
    {
      key: 'fullName',
      name: _t('Full Name'),
    },
    {
      key: 'email',
      name: _t('Email'),
    },
  ];

  actions = [
    {
      icon: 'info',
      link: (adminData: { id: string }) => {
        return `/internal/enterprise/user/${adminData.id}/memberships`;
      },
    },
  ];

  render() {
    const { errorMessage, slug, programId, thirdPartyOrganizationId } = this.state;

    return (
      <div className="rc-EnterpriseToolsHome">
        <Card>
          <h3>{_t('View Program Administrators:')}</h3>
          <TextBox
            placeholder={_t('Program Slug')}
            onUpdateField={this.handleUpdateText}
            currentValue={slug}
            inputProps={{ onKeyPress: this.onInputKeyPress, autoFocus: true }}
          />
          <br />
          <Button text={_t('See Administrators')} btnStyle="info" onClick={this.onViewProgramDetails} />
          {errorMessage && (
            <div>
              <br />
              <pre style={{ whiteSpace: 'pre-wrap' }}>{errorMessage}</pre>
            </div>
          )}
          {thirdPartyOrganizationId && programId && (
            <ThirdPartyOrganizationProviderBase thirdPartyOrganization={{ id: thirdPartyOrganizationId } as $TSFixMe}>
              <Admins programId={programId} columns={this.columns} actions={this.actions} />
            </ThirdPartyOrganizationProviderBase>
          )}
        </Card>
      </div>
    );
  }
}

const Admins = ({
  programId,
  columns,
  actions,
}: {
  programId: string;
  columns: { key: string; name: string }[];
  actions: { icon: string; link: (user: { id: string }) => string }[];
}) => {
  const { filteredTemplateIds } = useTemplateIdsQuery([]);
  const { allAdmins } = useAllAdminsQuery({
    templateIds: filteredTemplateIds,
    filteredProgramIds: programId ? [programId] : [],
    skip: !programId,
  });
  if (!allAdmins?.length || !programId) return null;
  return (
    <div>
      <h3>{_t('Admins')}</h3>
      <Table rows={allAdmins.map((_) => ({ ..._, id: _.userId }))} columns={columns} actions={actions} />
    </div>
  );
};

export default EnterpriseProgramLookup;
